import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

// Usuario anónimo por defecto
const ANONYMOUS_USER_EMAIL = "htabletcasa@gmail.com";

export function AuthProvider({ children }) {
  // Añadir nombre y tipo de usuario al estado inicial
  const [user, setUser] = useState({
    user_id: null,
    user_type: null,
    email: null,
    session_id: null,
    nombre: null,
    apellidos: null,
    isAnonymous: false,
    sub_rol: null, // Sub-rol del administrador
    promocode: null, // Código promocional aplicado
  });
  const [accessToken, setAccessToken] = useState(null);
  const [anonymousUser, setAnonymousUser] = useState(null);

  const clearSessionAndRedirectToAdminLogin = () => {
    localStorage.removeItem("auth-user");
    localStorage.removeItem("auth-token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_type");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_nombre");

    setUser({
      user_id: null,
      user_type: null,
      email: null,
      session_id: null,
      nombre: null,
      apellidos: null,
      isAnonymous: false,
      sub_rol: null,
      promocode: null,
    });
    setAccessToken(null);

    if (window.location.pathname !== "/admin/login-admin") {
      window.location.assign("/admin/login-admin");
    }
  };

  useEffect(() => {
    const isAdminPage = () => window.location.pathname.startsWith("/admin");

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response.status === 401 && isAdminPage()) {
        clearSessionAndRedirectToAdminLogin();
      }

      return response;
    };

    const axiosInterceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401 && isAdminPage()) {
          clearSessionAndRedirectToAdminLogin();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      window.fetch = originalFetch;
      axios.interceptors.response.eject(axiosInterceptorId);
    };
  }, []);

  // Cargar usuario anónimo desde el backend
  useEffect(() => {
    const fetchAnonymousUser = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/api/clientes/email/${ANONYMOUS_USER_EMAIL}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setAnonymousUser({
            user_id: data.id,
            user_type: 'cliente',
            email: data.email,
            nombre: data.nombre,
            apellidos: data.apellidos,
            isAnonymous: true,
          });
          console.log("[AuthContext] Usuario anónimo cargado:", data.email);
        }
      } catch (error) {
        console.error("[AuthContext] Error al cargar usuario anónimo:", error);
      }
    };

    fetchAnonymousUser();
  }, []);

  // Cargar usuario y token desde localStorage al iniciar
  useEffect(() => {
    const storedUser = localStorage.getItem("auth-user");
    const storedToken = localStorage.getItem("auth-token");
    localStorage.removeItem("access_token");

    console.log("Cargando desde localStorage:", { storedUser, storedToken });
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setAccessToken(storedToken);
      console.log("Estado inicial del accessToken:", storedToken);
    }
  }, []);

  // Guardar usuario y token en localStorage cuando cambian
  useEffect(() => {
    if (user && accessToken) {
      localStorage.setItem("auth-user", JSON.stringify(user));
      localStorage.setItem("auth-token", accessToken);
      localStorage.removeItem("access_token");
      console.log("Guardando en localStorage:", { user, accessToken });
    } else {
      localStorage.removeItem("auth-user");
      localStorage.removeItem("auth-token");
      localStorage.removeItem("access_token");
      console.log("Limpiando localStorage:", { user, accessToken });
    }
  }, [user, accessToken]);

  // Llamar esto tras login exitoso (recibe el objeto de la API)
  const login = (data) => {
    console.log("Datos recibidos en login:", data);
    setUser({
      user_id: data.user_id,
      user_type: data.user_type,
      email: data.email,
      session_id: data.session_id,
      nombre: data.nombre || null,
      apellidos: data.apellidos || null,
      isAnonymous: false,
      sub_rol: data.sub_rol || null, // Sub-rol del administrador
      promocode: data.promocode || null, // Código promocional
    });
    setAccessToken(data.access_token);
    console.log("Nuevo estado del accessToken tras login:", data.access_token);
  };

  // Llamar esto para cerrar sesión
  const logout = () => {
    setUser({
      user_id: null,
      user_type: null,
      email: null,
      session_id: null,
      nombre: null,
      apellidos: null,
      isAnonymous: false,
      sub_rol: null,
      promocode: null,
    });
    setAccessToken(null);
  };

  // Autenticar como usuario anónimo
  const loginAsAnonymous = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      
      const password = process.env.REACT_APP_ANONYMOUS_PASSWORD || 'anonymous2024';
      console.log("[AuthContext] Intentando autenticar usuario anónimo:", ANONYMOUS_USER_EMAIL);
      console.log("[AuthContext] Password a usar:", password);
      
      // Preparar device_info básico
      const deviceInfo = {
        custom_device_id: `anonymous-${Date.now()}`,
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        languages: navigator.languages.join(','),
      };
      
      const response = await fetch(`${apiUrl}/api/sesiones/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: ANONYMOUS_USER_EMAIL,
          password: password,
          device_info: deviceInfo,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          user_id: data.user_id,
          user_type: data.user_type,
          email: data.email,
          session_id: data.session_id,
          nombre: data.nombre || null,
          apellidos: data.apellidos || null,
          isAnonymous: true,
          sub_rol: data.sub_rol || null,
          promocode: user.promocode || null, // Mantener el promocode si existe
        });
        setAccessToken(data.access_token);
        console.log("[AuthContext] Usuario anónimo autenticado con token:", data.access_token);
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("[AuthContext] Error al autenticar usuario anónimo:", response.status, errorData);
        return false;
      }
    } catch (error) {
      console.error("[AuthContext] Error al autenticar usuario anónimo:", error);
      return false;
    }
  };

  const isAuthenticated = !!accessToken;

  // Función para obtener el usuario efectivo (autenticado o anónimo)
  const getEffectiveUser = () => {
    if (isAuthenticated && user?.user_id) {
      return user;
    }
    return anonymousUser;
  };

  // Función para establecer el código promocional en el contexto
  const setPromocode = (codigo) => {
    console.log("[AuthContext] Estableciendo código promocional:", codigo);
    setUser(prevUser => ({
      ...prevUser,
      promocode: codigo
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated,
        anonymousUser,
        getEffectiveUser,
        login,
        logout,
        loginAsAnonymous,
        setPromocode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook para usar el contexto
export function useAuth() {
  return useContext(AuthContext);
}